import React from 'react';
import clsx from 'clsx';

import IconClose from '@/assets/icons/icon-close.svg?react';
import IconSlash from '@/assets/icons/icon-slash.svg?react';

interface DataRow {
  label: string;
  value: string | number;
}

interface PanelSection {
  title?: string;
  rows: DataRow[];
  items?: Array<{
    name: string;
    rows: DataRow[];
  }>;
}

interface AnalyticsInspectorPanelProps {
  title: string;
  sections: PanelSection[];
  onClose?: () => void;
  className?: string;
}

export const AnalyticsInspectorPanel: React.FC<AnalyticsInspectorPanelProps> = ({
  title,
  sections,
  onClose,
  className,
}) => {
  return (
    <div
      className={clsx(
        'inspector bg-neutral-0 relative z-10 inline-flex h-full w-60 flex-col shadow-[-1px_0_0_0_var(--color-neutral-100)]',
        className,
      )}
    >
      {/* Header */}
      <div className="bg-neutral-0 inline-flex h-12 shrink-0 items-center justify-between self-stretch border-b border-neutral-100 px-3">
        <div className="flex items-center justify-start gap-2">
          <div className="text-xs leading-5 font-semibold text-neutral-900">{title}</div>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            aria-label="Close panel"
            className="inline-flex h-6 w-6 flex-col items-center justify-center gap-2.5 rounded-md transition-colors hover:bg-neutral-50"
          >
            <IconClose className="h-4 w-4 text-neutral-500" />
          </button>
        )}
      </div>

      {/* Content */}
      <div className="min-h-0 flex-1 overflow-y-auto [scrollbar-width:none]">
        <div className="flex flex-col items-center justify-center gap-3 self-stretch px-3 pt-3 pb-6">
          {sections.map((section, sectionIdx) => (
            <div
              key={sectionIdx}
              className="flex w-52 flex-col items-start justify-start gap-3 pt-1"
            >
              {/* Simple section (just rows) */}
              {section.rows.length > 0 && !section.items && (
                <>
                  {section.title && (
                    <div className="inline-flex w-52 items-center justify-start gap-2 pb-1">
                      <div className="text-xs font-semibold text-neutral-900">{section.title}</div>
                    </div>
                  )}
                  <div className="flex flex-col items-start justify-start self-stretch">
                    {section.rows.map((row, idx) => (
                      <div
                        key={idx}
                        className="inline-flex h-8 items-center justify-between self-stretch rounded-md"
                      >
                        <div className="text-xs leading-4 font-medium text-neutral-500">
                          {row.label}
                        </div>
                        <div className="flex items-center justify-start gap-5">
                          <div className="text-xs leading-4 font-medium text-neutral-500">
                            {row.value}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Grouped section (items with rows) */}
              {section.items && (
                <>
                  {section.items.map((item, itemIdx) => (
                    <div key={itemIdx} className="w-full">
                      {itemIdx > 0 && (
                        <div className="flex flex-col items-start justify-start gap-2.5 self-stretch py-1">
                          <div className="h-0 self-stretch border-t border-neutral-100" />
                        </div>
                      )}
                      <div className="flex w-52 flex-col items-start justify-start gap-3 pt-1">
                        <div className="inline-flex items-center justify-start gap-1">
                          <IconSlash className="h-4 w-4 text-neutral-500" />
                          <div className="text-xs font-semibold text-neutral-900">{item.name}</div>
                        </div>
                        <div className="flex flex-col items-start justify-start self-stretch">
                          {item.rows.map((row, rowIdx) => (
                            <div
                              key={rowIdx}
                              className="inline-flex h-8 items-center justify-between self-stretch rounded-md"
                            >
                              <div className="text-xs leading-4 font-medium text-neutral-500">
                                {row.label}
                              </div>
                              <div className="flex items-center justify-start gap-5">
                                <div className="text-xs leading-4 font-medium text-neutral-500">
                                  {row.value}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
